import { useCallback, useEffect, useLayoutEffect, useRef } from "react";

/**
 * Default window for coalescing socket-driven React Query invalidations.
 * Without a throttle, a per-entity socket event (a calendar sync re-extracting
 * hundreds of events on `calendar:events_changed`, a set of concurrent agent
 * threads each flipping `activityState` on `thread:mutated`) drives one refetch
 * per emission — a sustained storm that trips server rate limiting (BIG-5628,
 * BIG-6465).
 *
 * Exported so tests assert against the real value rather than a copy.
 */
export const DEFAULT_INVALIDATE_THROTTLE_MS = 2000;

/**
 * Default number of emissions that refetch immediately before the throttle
 * engages. A small flurry refetches eagerly (so single events feel instant); a
 * sustained burst refetches this many, then at most once per window. The
 * allowance refills once a full window passes without a successful flush.
 */
export const DEFAULT_INVALIDATE_BURST_ALLOWANCE = 5;

/**
 * What a `flush` may return. `false` reports that it did nothing; `void` and a
 * fire-and-forget `Promise` (what `invalidateQueries` returns) both count as
 * having acted. The union deliberately excludes `Promise<boolean>` so an
 * `async` flush can't return a `Promise<false>` that reads as having acted.
 */
export type FlushResult = false | void | Promise<void>;

/** Handle returned by {@link useThrottledInvalidation}. */
export type ThrottledInvalidation = {
  /** Call once per socket event. */
  trigger: () => void;
  /** Drop a scheduled trailing flush. See {@link useThrottledInvalidation}. */
  cancelPending: () => void;
};

/**
 * Timing guard for socket-driven React Query invalidation. Fires `flush`
 * immediately for the first `burstAllowance` emissions, then collapses a
 * sustained burst into at most one `flush` per `throttleMs`, refilling the
 * allowance once a full window passes without a successful flush.
 *
 * The caller owns *what* gets invalidated — it accumulates any per-event state
 * (e.g. a Set of changed ids) in its own ref and calls `trigger` per event; this
 * hook only decides *when* `flush` runs. A `flush` may report a no-op so an empty
 * one doesn't spend the window or the allowance ({@link FlushResult}).
 *
 * Only coalesce idempotent side effects like `invalidateQueries` ("refetch next
 * time" — collapsing N into one loses nothing). Never gate per-event
 * `setQueryData`, whose distinct payloads would be lost.
 *
 * Call `cancelPending` when the consumer's event source goes away while the
 * component stays mounted — a socket that nulls on disconnect. Unmount is
 * already handled. It drops only the scheduled flush, not the caller's pending
 * state, so accumulated work rides the next `trigger` rather than landing
 * mid-outage and stamping the window against the first post-reconnect event.
 *
 * @see https://tkdodo.eu/blog/using-web-sockets-with-react-query
 */
export const useThrottledInvalidation = (
  flush: () => FlushResult,
  {
    throttleMs = DEFAULT_INVALIDATE_THROTTLE_MS,
    burstAllowance = DEFAULT_INVALIDATE_BURST_ALLOWANCE,
  }: { throttleMs?: number; burstAllowance?: number } = {},
): ThrottledInvalidation => {
  // Throttle state: the pending trailing-edge timer, when we last flushed, and
  // how many immediate refetches this window spent.
  const trailingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastFlushAt = useRef(0);
  const burstCount = useRef(0);

  // Assigned at commit rather than during render: a render React discards must
  // not be able to install a callback that a later socket event or timer runs.
  const flushRef = useRef(flush);
  useLayoutEffect(() => {
    flushRef.current = flush;
  }, [flush]);

  /** Runs `flush`, reporting whether it actually acted. */
  const runFlush = useCallback(() => {
    // Stamp only on a real flush — a no-op flush (reported via `false`) must not
    // look like a refetch, which would suppress the next burst-allowance refill.
    // Wall clock rather than `performance.now()`, so fake timers can drive this.
    const didFlush = flushRef.current() !== false;
    if (didFlush) lastFlushAt.current = Date.now();
    return didFlush;
  }, []);

  const cancelPending = useCallback(() => {
    if (trailingTimer.current) {
      clearTimeout(trailingTimer.current);
      trailingTimer.current = null;
    }
  }, []);

  const trigger = useCallback(() => {
    if (trailingTimer.current) return; // a trailing flush is already scheduled; just accumulate

    // Clamped because `Date.now()` isn't monotonic: a backward clock step would
    // otherwise schedule the trailing timer a full skew out, wedging the
    // consumer. `performance.now()` would avoid this, but fake timers don't
    // advance it.
    const elapsed = Math.max(0, Date.now() - lastFlushAt.current);
    if (elapsed >= throttleMs) {
      // A full window since the last flush passed → refill and refetch now.
      if (runFlush()) burstCount.current = 1;
    } else if (burstCount.current < burstAllowance) {
      // Still within the burst allowance → refetch immediately. The allowance is
      // spent only when the flush acted, so no-op flushes can't exhaust it and
      // push a subsequent real flush onto the trailing timer.
      if (runFlush()) burstCount.current += 1;
    } else {
      // Allowance spent → coalesce into one trailing refetch at the boundary.
      trailingTimer.current = setTimeout(() => {
        trailingTimer.current = null;
        runFlush();
      }, throttleMs - elapsed);
    }
  }, [throttleMs, burstAllowance, runFlush]);

  // Drop a scheduled trailing flush if the consumer unmounts mid-window.
  useEffect(() => cancelPending, [cancelPending]);

  return { trigger, cancelPending };
};
